// shared/loot — secret: loot table format & evaluation, drop weighting.
// Phase 1+ implementation; Phase 0 stubs only.

export interface LootEntry {
  defId: string;
  weight: number;
  minStack: number;
  maxStack: number;
  tier: number;
}

export interface LootTable {
  id: string;
  rolls: number;
  entries: ReadonlyArray<LootEntry>;
}

export interface ItemStack {
  defId: string;
  count: number;
  tier: number;
}

export type Rng = () => number;

function pickEntry(entries: ReadonlyArray<LootEntry>, rng: Rng): LootEntry | null {
  const total = entries.reduce((s, e) => s + e.weight, 0);
  if (total <= 0) return null;
  let roll = rng() * total;
  for (const e of entries) {
    roll -= e.weight;
    if (roll <= 0) return e;
  }
  return entries[entries.length - 1] ?? null;
}

export function draw(table: LootTable, rng: Rng): ItemStack[] {
  const stacks: ItemStack[] = [];
  for (let i = 0; i < table.rolls; i++) {
    const e = pickEntry(table.entries, rng);
    if (!e) continue;
    const range = e.maxStack - e.minStack;
    const count = e.minStack + Math.floor(rng() * (range + 1));
    if (count <= 0) continue;
    stacks.push({ defId: e.defId, count, tier: e.tier });
  }
  // Merge same-defId stacks for cleaner inventory.
  const merged = new Map<string, ItemStack>();
  for (const s of stacks) {
    const key = `${s.defId}@${s.tier}`;
    const existing = merged.get(key);
    if (existing) {
      merged.set(key, { ...existing, count: existing.count + s.count });
    } else {
      merged.set(key, { ...s });
    }
  }
  return Array.from(merged.values());
}

// Deterministic RNG factory for reproducibility — not exported as the canonical
// project Rng (callers can provide their own); useful for tests and seeded
// gameplay (e.g. server replay of client claims).
export function seededRng(seed: number): Rng {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
